<template>
  <div :class="['form-group', { 'no-wrapper': noWrapper }]">
    <label v-if="label && !noWrapper" :for="id">
      {{ label }}
      <span v-if="required" class="required-indicator" aria-label="requis">*</span>
      <Tooltip v-if="info" :content="info" placement="top">
        <i
          class="fas fa-info-circle label-info-icon"
          aria-label="Informations"
          tabindex="-1"
        ></i>
      </Tooltip>
    </label>
    <textarea
      ref="textareaRef"
      :id="id"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :required="required"
      :rows="rows"
      :minlength="minlength"
      :maxlength="maxlength"
      :data-testid="testId ? testId : undefined"
      :class="['form-control', textareaClass, {
        'form-control-error': error
      }]"
      :aria-label="ariaLabel || (noWrapper && !label ? placeholder : undefined)"
      :aria-describedby="errorId || (hint ? hintId : undefined)"
      :aria-invalid="error ? 'true' : undefined"
      :aria-required="required"
      @input="handleInput"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    ></textarea>
    <span v-if="error && !noWrapper" :id="errorId" class="error-text" role="alert" aria-live="polite">{{ error }}</span>
    <span v-if="hint && !error && !noWrapper" :id="hintId" class="hint-text">{{ hint }}</span>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch, onMounted } from 'vue'
import Tooltip from './Tooltip.vue'

const textareaRef = ref(null)

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  label: {
    type: String,
    default: null
  },
  placeholder: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  rows: {
    type: [String, Number],
    default: 4
  },
  minlength: {
    type: [String, Number],
    default: null
  },
  maxlength: {
    type: [String, Number],
    default: null
  },
  error: {
    type: String,
    default: null
  },
  hint: {
    type: String,
    default: null
  },
  id: {
    type: String,
    default: () => `textarea-${Math.random().toString(36).substr(2, 9)}`
  },
  noWrapper: {
    type: Boolean,
    default: false
  },
  ariaLabel: {
    type: String,
    default: null
  },
  testId: {
    type: String,
    default: null
  },
  info: {
    type: String,
    default: null
  },
  trim: {
    type: Boolean,
    default: false
  },
  autoResize: {
    type: Boolean,
    default: false
  },
  // Classe additionnelle reportée sur le <textarea> (layout : largeur/flex…)
  textareaClass: {
    type: [String, Array, Object],
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'blur', 'focus'])

const errorId = computed(() => props.error ? `${props.id}-error` : null)
const hintId = computed(() => props.hint ? `${props.id}-hint` : null)

const resize = () => {
  const el = textareaRef.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

const handleInput = (e) => {
  const value = props.trim ? e.target.value.trim() : e.target.value
  emit('update:modelValue', value)
  if (props.autoResize) nextTick(resize)
}

if (props.autoResize) {
  watch(() => props.modelValue, () => nextTick(resize))
  onMounted(() => nextTick(resize))
}

defineExpose({
  textareaRef
})
</script>

<style scoped>
/* Les styles .form-group, .form-control, .error-text, .hint-text proviennent de styles/forms.css */
/* On garde uniquement les styles spécifiques au composant */
textarea.form-control {
  resize: vertical;
  font-family: inherit;
}

.form-control-error {
  border-color: var(--color-danger);
}

.no-wrapper {
  margin-bottom: 0;
}

.no-wrapper label {
  display: none;
}

.label-info-icon {
  margin-left: 6px;
  cursor: help;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  transition: color var(--transition-base);
  vertical-align: middle;
}

.label-info-icon:hover {
  color: var(--color-primary);
}

label :deep(.tooltip-wrapper) {
  display: inline-flex;
  width: auto;
}
</style>
